import qs from "query-string";

export const LEGACY_MUNIN_INTERFACE_NAME = "MachineUiInterface";
export const MOCK_LEGACY_MUNIN_INTERFACE_URL = "wss://localhost:27720";

const isRealLegacyInterfaceDetected = (): boolean => {
  // TODO: Look for other user agents? Maybe look for a substring?
  return navigator.userAgent === "android";
};

const shouldStartMockInterface = (): boolean => {
  const query = qs.parse(window.location.search);
  if (query.mockLegacyMuninInterface === undefined) {
    return false;
  } else if (query.mockLegacyMuninInterface) {
    // Query result could, from a type perspective, return a list rather
    // than just a string, so handle that in all the query processing that
    // follows
    const p =
      typeof query.mockLegacyMuninInterface === "object"
        ? String(query.mockLegacyMuninInterface[0])
        : query.mockLegacyMuninInterface;
    return ["true", "1"].includes(p.toLowerCase());
  }
  return false;
};

export const isLegacyMuninInterfaceDetected = (): boolean => {
  return shouldStartMockInterface() || isRealLegacyInterfaceDetected();
};

export interface LegacyMessageType {
  sinkType: string;
  payload?: unknown;
}

export class LegacyMuninInterface {
  private isConnected = false;

  constructor(onMessage: (message: LegacyMessageType) => void) {
    console.debug("Trying to create the LegacyMuninInterface");
    const windowWithInterface = window as any;

    if (shouldStartMockInterface()) {
      console.debug("Using a WebSocket connection to mock the legacy Munin interface");
      windowWithInterface[LEGACY_MUNIN_INTERFACE_NAME] = new WebSocket(MOCK_LEGACY_MUNIN_INTERFACE_URL);
      windowWithInterface[LEGACY_MUNIN_INTERFACE_NAME].onopen = () => {
        console.debug("WebSocket connection to mock the legacy Munin interface is open");
        this.isConnected = true;
      };
    } else if (isRealLegacyInterfaceDetected()) {
      console.debug("Connected to a real Legacy Munin interface!");
      this.isConnected = true;
    }

    // If there is an interface present, either real or mocked, set up
    // handling of inbound messages sent from the other side of the interface.
    if (windowWithInterface[LEGACY_MUNIN_INTERFACE_NAME]) {
      this.addMessageHandler(onMessage);
      console.debug("Created handler for inbound messages on the legacy Munin interface");
    }
  }

  addMessageHandler = (onMessage: (message: LegacyMessageType) => void): void => {
    // Note here the lower-case of the onmessage method, this follows the
    // WebSocket API, where camelCase is not used.
    (window as any)[LEGACY_MUNIN_INTERFACE_NAME].onmessage = (event: any) => {
      // Ideally the data coming across the interface would always be in the
      // same format, however, it appears that Munin actually sends an
      // object with only the payload as JSON, while the mock interface
      // sends the entire message as a JSON string...here we handle the
      // different cases.
      let data;
      if (typeof event.data === "string") {
        data = JSON.parse(event.data);
      } else if (typeof event.data === "object") {
        data = event.data;
        // TODO: Update Munin-side interface so extra parsing not needed
        if (data.payload) {
          data.payload = JSON.parse(data.payload);
        }
      }

      // Once the data is extracted from the message, call the onMessage
      // function that will do whatever should be done, e.g., send an event
      onMessage(data);
    };
  };

  sendMessage = (message: Record<string, unknown>, onmessage = () => null): void => {
    // TODO: Handle not connected better?
    if (!this.isConnected) {
      console.log("Not yet connect to an interface, message discarded");
      return;
    }

    // Asynchronously send the message across the interface but return
    // right away in order to not block UI.
    // TODO: Add tracing?
    new Promise<void>((resolve) => {
      (window as any)[LEGACY_MUNIN_INTERFACE_NAME].send(JSON.stringify(message));
      resolve();
    }).catch((error) => {
      // TODO: What to do when this fails? We end up dropping data if we do nothing...
      console.error(error);
    });
  };

  requestIdentity = (): void => this.sendMessage({ message: "get identity" });
  showAboutPage = (): void => this.sendMessage({ message: "show about page" });
  setSticky = (enable: boolean): void => this.sendMessage({ message: "enable sticky", enable });
  restartBluetooth = (): void => this.sendMessage({ message: "restart bluetooth" });
  restartIoio = (): void => this.sendMessage({ message: "restart ioio" });
  flushTelemetry = (): void => this.sendMessage({ message: "send telemetry" });
  getConfigs = (): void => this.sendMessage({ message: "force get configs" });
  setLight = (light: string): void => this.sendMessage({ message: "attention level", attentionLevel: light });
  sendMeasurement = ({
    measurement,
    sourceType,
    sourceId,
    timestamp,
  }: {
    measurement: any;
    sourceType: string;
    sourceId: string;
    timestamp: number;
  }): void =>
    this.sendMessage({
      message: "add measurement",
      sourceType,
      id: sourceId,
      timestamp,
      measurement: JSON.stringify(measurement),
    });
}
